import formatLocationLabel from '@/utils/formatLocationLabel';
import type {
  AudienceFilter,
  ProfileFilter,
  PlatformFilters,
} from '@/types/models/creator-crm/creatorCrm';
import type {
  FiltersGroupId,
  PlatformId,
} from '@/types/models/creator-crm/filterId';
import type {
  SegmentFilters,
  SegmentMultiPlatformFilter,
  SegmentHandleFilter,
} from '@/types/models/segments/segment';
import {isHandleFilterAllPlatforms} from './compareFiltersWithSegment';

type AppliedFilters = Partial<Record<string, unknown>>;
type AppliedFiltersGroups = Partial<Record<FiltersGroupId, AppliedFilters>>;

export type SegmentFiltersApplyResult = {
  appliedFiltersGroups: AppliedFiltersGroups;
  searchState?: {
    type: 'name' | 'handle' | 'bio' | 'content' | 'all';
    chips: string[];
  };
};

/**
 * Maps segment filters (camelCase) to CRM applied filters format.
 * Used by applySegmentFilters to apply a segment's filters to the store.
 * Bio, compound, and captions go only to searchState (shown in ActiveFiltersBar);
 * they are not written to internal filter state (creatorDetails/keywords).
 */
export function segmentFiltersToAppliedFiltersGroups(
  segmentFilters: SegmentFilters
): SegmentFiltersApplyResult {
  if (segmentFilters.type === 'HandleFilter') {
    return mapHandleFilterToApplied(segmentFilters);
  }
  return mapMultiPlatformFilterToApplied(segmentFilters);
}

function mapHandleFilterToApplied(
  filter: SegmentHandleFilter
): SegmentFiltersApplyResult {
  const result: SegmentFiltersApplyResult = {
    appliedFiltersGroups: {
      platforms: {},
    },
    searchState: {type: 'handle', chips: [filter.handle]},
  };
  const platformId: PlatformId = isHandleFilterAllPlatforms(filter.platforms)
    ? 'all'
    : (filter.platforms![0] as PlatformId);
  result.appliedFiltersGroups.platforms!.platform = platformId;
  result.appliedFiltersGroups.platforms!.platformMetrics = [platformId, {}];
  return result;
}

function mapMultiPlatformFilterToApplied(
  filter: SegmentMultiPlatformFilter
): SegmentFiltersApplyResult {
  const appliedFiltersGroups: AppliedFiltersGroups = {};
  let searchState: SegmentFiltersApplyResult['searchState'];

  if (filter.profile) {
    appliedFiltersGroups.creatorDetails = mapProfileToCreatorDetails(
      filter.profile
    );
  }
  if (filter.vetting?.decisions?.length) {
    appliedFiltersGroups.creatorDetails =
      appliedFiltersGroups.creatorDetails ?? {};
    appliedFiltersGroups.creatorDetails.vettingDecision =
      filter.vetting.decisions;
  }
  if (filter.campaignHistory?.scope) {
    appliedFiltersGroups.creatorDetails =
      appliedFiltersGroups.creatorDetails ?? {};
    appliedFiltersGroups.creatorDetails.campaignHistory =
      filter.campaignHistory.scope;
  }
  // captions, bio, compoundName, and handle go only to searchState, not to keywords/creatorDetails
  if (filter.handle) {
    searchState = {type: 'handle', chips: [filter.handle]};
  } else if (filter.compoundName) {
    searchState = {type: 'name', chips: [filter.compoundName]};
  } else if (filter.compound?.length) {
    searchState = {type: 'all', chips: filter.compound};
  } else if (filter.profile?.bio?.length) {
    searchState = {type: 'bio', chips: filter.profile.bio};
  } else if (filter.profile?.captions?.length) {
    searchState = {type: 'content', chips: filter.profile.captions};
  } else {
    searchState = {type: 'all', chips: []};
  }
  if (filter.audience || filter.platforms) {
    appliedFiltersGroups.platforms = appliedFiltersGroups.platforms ?? {};
    if (filter.audience) {
      Object.assign(
        appliedFiltersGroups.platforms,
        mapAudienceToPlatformFilters(filter.audience)
      );
    }
    if (filter.platforms) {
      const [platformId, platformMetrics] =
        mapSegmentPlatformsToPlatformMetrics(filter.platforms);
      if (platformId) {
        appliedFiltersGroups.platforms.platform = platformId;
        appliedFiltersGroups.platforms.platformMetrics = [
          platformId,
          platformMetrics,
        ];
      }
    }
  }

  return {appliedFiltersGroups, searchState};
}

function locationToOption(loc: {
  longitude?: number;
  latitude?: number;
  place?: string;
  region?: string;
  country?: string;
}): {value: unknown; id: string; localeLabelKey: string} {
  return {
    value: loc,
    id: locationToId(loc),
    localeLabelKey: locationToDisplayText(loc),
  };
}

/**
 * CRM multiSelectionAsync expects selected to be option-shaped ({ value, id, localeLabelKey }).
 * mapToType extracts option.value, so we wrap location objects as options.
 */
function locationsToOptionShaped(
  locations: ProfileFilter['locations']
): {value: unknown; id: string; localeLabelKey: string}[] | undefined {
  if (!locations?.length) return undefined;
  return locations.map(locationToOption);
}

function locationToId(loc: {
  longitude?: number;
  latitude?: number;
  place?: string;
  region?: string;
  country?: string;
}): string {
  if (loc.place != null) return `place:${loc.place}`;
  if (loc.region != null) return `region:${loc.region}`;
  if (loc.country != null) return `country:${loc.country}`;
  if (loc.longitude != null && loc.latitude != null)
    return `coords:${loc.latitude},${loc.longitude}`;
  return JSON.stringify(loc);
}

/** Display text for chip; translate() will show this when no translation exists. */
function locationToDisplayText(loc: {
  place?: string;
  region?: string;
  country?: string;
}): string {
  return formatLocationLabel(loc);
}

/**
 * Maps segment profile to creatorDetails applied filters only.
 * Bio is excluded; it goes only to searchState (shown in ActiveFiltersBar).
 */
function mapProfileToCreatorDetails(profile: ProfileFilter): AppliedFilters {
  const applied: AppliedFilters = {};
  if (profile.gender) applied.gender = profile.gender;
  if (profile.age) {
    applied.age = {
      min: profile.age.fromAge,
      max: profile.age.toAge,
    };
  }
  const locationOptions = locationsToOptionShaped(profile.locations);
  if (locationOptions?.length) applied.location = locationOptions;
  if (profile.categories?.length) applied.category = profile.categories;
  // profile.bio is not applied here; it goes only to searchState
  return applied;
}

function mapAudienceToPlatformFilters(
  audience: AudienceFilter
): AppliedFilters {
  const applied: AppliedFilters = {};
  if (audience.gender) {
    applied.gender = [audience.gender.value, audience.gender.weight];
  }
  if (audience.isLdaCompliant === true) {
    applied.age = true;
  } else if (audience.ages?.length) {
    applied.age = audience.ages.map(
      (a) => [a.value, a.weight] as [unknown, number | undefined]
    );
  }
  const audienceLocationOptions = audience.locations?.length
    ? audience.locations.map(locationToOption)
    : undefined;
  if (audienceLocationOptions?.length)
    applied.location = audienceLocationOptions;
  return applied;
}

function mapSegmentPlatformsToPlatformMetrics(
  platforms: PlatformFilters
): [PlatformId | null, Record<string, unknown>] {
  // Accept any platform entry (including empty {} so e.g. platforms: { instagram: {} } still selects Instagram)
  const entry = Object.entries(platforms).find(([_, v]) => v != null);
  if (!entry) return [null, {}];
  const [platformId, platformFilter] = entry;
  const metrics: Record<string, unknown> = {};
  if (platformFilter?.followers) {
    metrics.followers = platformFilter.followers;
  }
  if (platformFilter?.engagementRate !== undefined) {
    metrics.engagementRate = platformFilter.engagementRate;
  }
  if (
    platformFilter &&
    'viewsCount' in platformFilter &&
    platformFilter.viewsCount
  ) {
    metrics.averageViews = platformFilter.viewsCount;
  }
  return [platformId as PlatformId, metrics];
}
